/**
 * The fields that describe one variant: its name, environment, base URL
 * and optional browser overrides.
 *
 * Two places ask for these: the New Variant / Edit Variant dialog on the
 * Apps page, and step 2 of the New App dialog (`NewAppWizard`). They share
 * this one form so the two can't drift apart, which is the failure a copy
 * of the fields would invite the first time someone adds one.
 *
 * The form is controlled: the caller owns a `VariantFormState` and gets
 * the whole next state back on every change. Saving stays with the
 * caller, because the two callers save differently (create or update,
 * scan or not, one save or two).
 */
import { Input, Label, Select } from '@/components/ui'
import type { Variant, VariantStatus } from '@/types'

const ENVIRONMENT_OPTIONS = [
  { value: 'development', label: 'Development' },
  { value: 'testing', label: 'Testing' },
  { value: 'training', label: 'Training' },
  { value: 'staging', label: 'Staging' },
  { value: 'production', label: 'Production' },
  { value: 'custom', label: 'Custom' },
]

/** What the variant form holds while it is being edited. Viewport sizes
 * stay strings until save, so a half-typed number is not rewritten under
 * the user's cursor. */
export interface VariantFormState {
  name: string
  baseUrl: string
  status: VariantStatus
  timezone: string
  locale: string
  viewportWidth: string
  viewportHeight: string
  browserOpen: boolean
}

/** Compute the starting form state for a new variant OR for editing an
 * existing one. A pure helper so the prefill rules are testable without
 * rendering a page.
 *
 * - Pass `null` (or nothing) for fresh defaults. Slow-motion is not
 *   prefilled: it is a run template setting, so a hidden variant default
 *   would silently pace every run through the server-side fallback.
 * - Pass an existing variant for its stored values. `browserOpen` says
 *   whether ANY browser override is set, so a set override is never
 *   hidden behind a collapsed section. */
export function computeVariantFormState(variant?: Variant | null): VariantFormState {
  if (variant) {
    const b = variant.browser
    return {
      name: variant.name,
      baseUrl: variant.baseUrl,
      status: variant.status,
      timezone: b?.timezone ?? '',
      locale: b?.locale ?? '',
      viewportWidth: b?.viewportWidth ? String(b.viewportWidth) : '',
      viewportHeight: b?.viewportHeight ? String(b.viewportHeight) : '',
      browserOpen: Boolean(b?.timezone || b?.locale || b?.viewportWidth || b?.viewportHeight),
    }
  }
  return {
    name: '',
    baseUrl: '',
    status: 'development',
    timezone: '',
    locale: '',
    viewportWidth: '',
    viewportHeight: '',
    browserOpen: false,
  }
}

/** The browser overrides to save, from what the form holds. Blank fields
 * and non-positive sizes are left out, so they inherit the test runner's
 * defaults. Undefined when nothing is set. */
export function browserOverridesFrom(form: VariantFormState): Variant['browser'] {
  const browser: NonNullable<Variant['browser']> = {}
  if (form.timezone.trim()) browser.timezone = form.timezone.trim()
  if (form.locale.trim()) browser.locale = form.locale.trim()
  const w = parseInt(form.viewportWidth, 10)
  const h = parseInt(form.viewportHeight, 10)
  if (Number.isFinite(w) && w > 0) browser.viewportWidth = w
  if (Number.isFinite(h) && h > 0) browser.viewportHeight = h
  return Object.keys(browser).length > 0 ? browser : undefined
}

/** The variant fields to save, from what the form holds. Every path that
 * creates or updates a variant from this form saves through here, so a
 * field added to the form is saved by all of them. */
export function variantFieldsFrom(form: VariantFormState): Pick<Variant, 'name' | 'baseUrl' | 'status' | 'browser'> {
  return {
    name: form.name,
    baseUrl: form.baseUrl,
    status: form.status,
    browser: browserOverridesFrom(form),
  }
}

/** Whether the form has what a save needs: a name and a base URL. */
export function isVariantFormComplete(form: VariantFormState): boolean {
  return Boolean(form.name && form.baseUrl)
}

export function VariantFormFields({
  value,
  onChange,
  disabled = false,
}: {
  value: VariantFormState
  onChange: (next: VariantFormState) => void
  /** Set while the caller is saving, so nothing changes under a save. */
  disabled?: boolean
}) {
  const set = <K extends keyof VariantFormState>(key: K, v: VariantFormState[K]) => onChange({ ...value, [key]: v })

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Name</Label>
          <Input
            value={value.name}
            onChange={(e) => set('name', e.target.value)}
            placeholder="Production"
            disabled={disabled}
          />
        </div>
        <div className="space-y-2">
          <Label>Environment</Label>
          <Select
            options={ENVIRONMENT_OPTIONS}
            value={value.status}
            onChange={(e) => set('status', e.target.value as VariantStatus)}
            disabled={disabled}
          />
        </div>
      </div>
      <div className="space-y-2">
        <Label>Base URL</Label>
        <Input
          value={value.baseUrl}
          onChange={(e) => set('baseUrl', e.target.value)}
          placeholder="https://app.example.com"
          disabled={disabled}
        />
      </div>

      <div className="space-y-2">
        <button
          type="button"
          onClick={() => set('browserOpen', !value.browserOpen)}
          disabled={disabled}
          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground disabled:opacity-50"
        >
          {value.browserOpen ? '▾' : '▸'} Browser overrides (optional)
        </button>
        {value.browserOpen && (
          <div className="grid grid-cols-2 gap-3 pt-1 pl-4 border-l border-border/40">
            <div className="space-y-1">
              <Label className="text-xs">Timezone</Label>
              <Input
                value={value.timezone}
                onChange={(e) => set('timezone', e.target.value)}
                placeholder="America/New_York"
                disabled={disabled}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Locale</Label>
              <Input
                value={value.locale}
                onChange={(e) => set('locale', e.target.value)}
                placeholder="en-US"
                disabled={disabled}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Viewport width</Label>
              <Input
                type="number"
                value={value.viewportWidth}
                onChange={(e) => set('viewportWidth', e.target.value)}
                placeholder="1280"
                disabled={disabled}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Viewport height</Label>
              <Input
                type="number"
                value={value.viewportHeight}
                onChange={(e) => set('viewportHeight', e.target.value)}
                placeholder="720"
                disabled={disabled}
              />
            </div>
            {/* Pacing is not a variant setting: slow-motion and the pause
                between tests live on the run templates. */}
            <p className="col-span-2 text-[10px] text-muted-foreground">
              Timezone, locale, and viewport inherit the test-runner defaults when blank.
              Pacing — slow-motion and pause between tests — is set on the run templates.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
